import axios, {
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { getConfig } from '@config/env';
import { femaCapture } from '../fema/capture';
import { UNAUTHORIZED_EVENT, type UnauthorizedEventDetail } from '../constants/events';
import { getLogoutSource } from '../lib/logoutSource';
import { buildCacheKey, DEFAULT_MAX_AGE_MS, readFromCache, writeToCache } from './cache';
import { authEndpoints, customerEndpoints, onboardingEndpoints } from './endpoints';

export interface HttpRequestConfig<D = unknown> extends AxiosRequestConfig<D> {
  /**
   * When true, the request interceptor will not auto-attach the access token.
   * Useful for endpoints that require a different bearer (e.g. refresh token),
   * or when the caller sets Authorization explicitly.
   */
  skipAuth?: boolean;
}

// Type definitions
// interface _HttpConfig {
//   apiBaseUrl: string;
//   requestTimeout?: number;
// }

// interface _SafeHttpRequestOptions {
//   method: string;
//   url: string;
//   data?: any;
//   config?: AxiosRequestConfig;
// }

interface SessionExpiredPayload {
  readonly $error?: string;
  readonly _error?: string;
  readonly sessionExpired: 'Y';
  readonly errorTitle?: string;
  readonly errorMessage?: string;
}

class SessionExpiredError extends Error {
  readonly url: string | undefined;
  readonly method: string | undefined;

  constructor(message: string, url?: string, method?: string) {
    super(message);
    this.name = 'SessionExpiredError';
    this.url = url;
    this.method = method;
  }
}

const SESSION_EXPIRED_DEFAULT_MESSAGE = 'Your session has expired. Please sign in again.';

// ============================================================================
// TOKEN REFRESH MANAGEMENT
// Handles automatic token refresh when 401 errors occur
// ============================================================================

interface FailedQueueItem {
  resolve: (value: AxiosResponse | PromiseLike<AxiosResponse>) => void;
  reject: (reason?: unknown) => void;
  config: InternalAxiosRequestConfig;
}

type IeHubInternalAxiosRequestConfig = InternalAxiosRequestConfig & {
  readonly skipAuth?: boolean;
  _retry?: boolean;
};

// State for managing token refresh (per-tab — see refresh-lock helpers below
// for the cross-tab/reload-surviving lock).
let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];

// Promise-based de-dup for non-interceptor callers (e.g. SessionExpiryManager's
// "Keep me signed in"). When set, all callers of `refreshAccessTokenCoordinated`
// receive the same in-flight Promise instead of starting a parallel refresh —
// which would race the interceptor's path and submit the same refresh token
// twice, tripping the backend's reuse-detection.
let inFlightRefresh: Promise<string | null> | null = null;

// ── Cross-tab / cross-reload refresh lock ────────────────
// The in-memory `isRefreshing` flag does NOT survive page reloads (notably
// the OB OAuth bounce, which navigates away and back), and does NOT
// coordinate across tabs. Without a persisted lock, two refresh attempts
// can race, the loser submits a stale refresh token, and the backend's
// reuse-detection wipes the entire token family — logging the user out.
// The localStorage-backed lock + storage-event coordination below ensures
// only one refresh runs at a time per browser/profile.
const REFRESH_LOCK_KEY = 'authRefreshLock';
const REFRESH_LOCK_STALE_MS = 10_000; // an in-flight refresh older than this is presumed dead
const REFRESH_WAIT_TIMEOUT_MS = 12_000; // give up waiting on another tab after this

interface RefreshLock {
  owner: string;
  acquiredAt: number;
}

const safeLocalStorageGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore storage errors (e.g., blocked storage contexts)
  }
};

const safeLocalStorageRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore storage errors (e.g., blocked storage contexts)
  }
};

const safeSessionStorageGetItem = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

// ── Admin/debug log viewers ──────────────────────────────
// The webhook-logs and api-failures viewers are gated server-side by the admin
// key (requireAdminKey middleware). The RequireAdminKey gate stores the
// operator-entered key in sessionStorage under this key; the request interceptor
// passes it in the `Authorization` header (scheme "AdminKey") ONLY to those admin
// endpoints — never to ordinary customer traffic. We use Authorization (already
// on the cross-origin nginx proxy's CORS allow-list) instead of a custom
// X-Admin-Key header (blocked by preflight) or a query param (which would leak
// the key into proxy/access logs). These endpoints require no login, so the
// Authorization header is free to carry the key.
const ADMIN_KEY_STORAGE_KEY = 'uwAdminKey';
const ADMIN_KEY_URL_PREFIXES = [
  '/iehub-customer/api/v1/uw/webhook-logs',
  '/iehub-customer/api/v1/uw/admin/verify-key',
  '/api/admin/failures',
];
const needsAdminKey = (url: string | undefined): boolean =>
  typeof url === 'string' && ADMIN_KEY_URL_PREFIXES.some((prefix) => url.startsWith(prefix));

const readRefreshLock = (): RefreshLock | null => {
  const raw = safeLocalStorageGetItem(REFRESH_LOCK_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<RefreshLock>;
    if (typeof parsed.owner !== 'string' || typeof parsed.acquiredAt !== 'number') {
      return null;
    }
    return { owner: parsed.owner, acquiredAt: parsed.acquiredAt };
  } catch {
    return null;
  }
};

const writeRefreshLock = (lock: RefreshLock): void => {
  safeLocalStorageSetItem(REFRESH_LOCK_KEY, JSON.stringify(lock));
};

const clearRefreshLock = (owner?: string): void => {
  // Only release the lock if we still own it (don't accidentally clear
  // another tab's freshly-acquired lock).
  if (owner) {
    const current = readRefreshLock();
    if (!current || current.owner !== owner) return;
  }
  safeLocalStorageRemoveItem(REFRESH_LOCK_KEY);
};

const isLockFresh = (lock: RefreshLock | null): boolean =>
  !!lock && Date.now() - lock.acquiredAt < REFRESH_LOCK_STALE_MS;

/**
 * Best-effort atomic acquire of the cross-tab refresh lock.
 * Returns the owner id we wrote on success, null if another tab holds it.
 * The "best-effort" caveat: if two tabs write within the same microtask
 * window, whichever's write resolves last wins — both reads will see the
 * same owner. The 250ms poll in `waitForOtherTabRefresh` covers the rare
 * tie loser ending up neither acquired nor waiting.
 */
const tryAcquireRefreshLock = (): string | null => {
  const existing = readRefreshLock();
  if (isLockFresh(existing)) return null;

  const owner =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random()}`;
  writeRefreshLock({ owner, acquiredAt: Date.now() });

  // Re-read to detect a concurrent writer that overwrote us.
  const after = readRefreshLock();
  if (after?.owner !== owner) return null;
  return owner;
};

/**
 * Wait for another tab/context to finish refreshing. Resolves with the
 * new access token if one appears in localStorage, or null if the wait
 * times out / the lock goes stale (caller should fall through to its own
 * refresh attempt).
 */
const waitForOtherTabRefresh = (priorToken: string | null): Promise<string | null> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve(null);
      return;
    }

    let settled = false;
    const finish = (token: string | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('storage', onStorage);
      clearInterval(pollHandle);
      clearTimeout(timeoutHandle);
      resolve(token);
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'authToken' && typeof e.newValue === 'string' && e.newValue && e.newValue !== priorToken) {
        finish(e.newValue);
      }
    };

    const pollHandle = setInterval(() => {
      const current = safeLocalStorageGetItem('authToken');
      if (current && current !== priorToken) {
        finish(current);
        return;
      }
      // If the lock is no longer fresh, the holder probably died — bail
      // out so the caller can take over the refresh itself.
      if (!isLockFresh(readRefreshLock())) {
        finish(null);
      }
    }, 250);

    const timeoutHandle = setTimeout(() => finish(null), REFRESH_WAIT_TIMEOUT_MS);

    window.addEventListener('storage', onStorage);
  });
};

/**
 * Process the queue of failed requests after token refresh
 * @param error - Error if refresh failed, null if successful
 * @param token - New access token if refresh succeeded
 */
const processQueue = (error: Error | null, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else if (token) {
      // Update the config with new token and retry
      config.headers.Authorization = `Bearer ${token}`;
      resolve(axios.request(config));
    }
  });
  failedQueue = [];
};

/**
 * Attempt to refresh the access token using the stored refresh token
 * This function is called directly to avoid circular imports with AuthService
 * @returns New access token if successful, null if failed
 */
const tryRefreshToken = async (): Promise<string | null> => {
  const refreshToken = safeLocalStorageGetItem('refreshToken');
  
  if (!refreshToken) {
    console.log('🔐 Token refresh: No refresh token available');
    return null;
  }

  try {
    console.log('🔐 Token refresh: Attempting to refresh access token...');
    
    // Call refresh endpoint directly to avoid circular dependency with AuthService
    // Use axios directly without interceptors to prevent infinite loops
    const config = getConfig();
    const baseURL = (config.api.baseURL || '').replace(/\/+$/, '');
    const response = await axios.post<unknown>(
      `${baseURL}${authEndpoints.refresh}`,
      { refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
          // Backend also accepts refresh token via Authorization bearer
          Authorization: `Bearer ${refreshToken}`,
        },
        timeout: 10000,
      }
    );

    // Backend envelope: { success, data: { token, refreshToken, user, expiresAt } }
    // Also tolerate a flat shape, or the legacy snake_case shape.
    const raw = response.data as Record<string, unknown> | undefined;
    const inner = (raw && typeof raw === 'object' && 'data' in raw
      ? (raw as { data: unknown }).data
      : raw) as Record<string, unknown> | undefined;

    const newAccess =
      (inner?.token as string | undefined) ||
      (inner?.access_token as string | undefined);
    const newRefresh =
      (inner?.refreshToken as string | undefined) ||
      (inner?.refresh_token as string | undefined);

    if (newAccess) {
      safeLocalStorageSetItem('authToken', newAccess);
      if (newRefresh) {
        safeLocalStorageSetItem('refreshToken', newRefresh);
      }
      console.log('🔐 Token refresh: Successfully refreshed tokens');
      return newAccess;
    }

    console.log('🔐 Token refresh: No access token in response', raw);
    return null;
  } catch (error) {
    console.log('🔐 Token refresh: Failed to refresh token', error);
    // Clear tokens on refresh failure
    safeLocalStorageRemoveItem('authToken');
    safeLocalStorageRemoveItem('refreshToken');
    return null;
  }
};

/**
 * Coordinated refresh for callers outside the response interceptor. Joins the
 * same per-tab in-flight Promise and the cross-tab localStorage lock that the
 * 401 interceptor uses, so a manual "Keep me signed in" cannot race an
 * automatic 401-driven refresh and submit the same refresh token twice.
 * Returns the new access token, or null if the refresh failed.
 */
export async function refreshAccessTokenCoordinated(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  // Interceptor is already mid-refresh in this tab. Wait for it to land the
  // new token in localStorage rather than starting a parallel refresh.
  if (isRefreshing) {
    const priorToken = safeLocalStorageGetItem('authToken');
    return await waitForOtherTabRefresh(priorToken);
  }

  const promise = (async (): Promise<string | null> => {
    isRefreshing = true;
    try {
      const priorToken = safeLocalStorageGetItem('authToken');
      let lockOwner = tryAcquireRefreshLock();
      if (!lockOwner) {
        const newToken = await waitForOtherTabRefresh(priorToken);
        if (newToken) return newToken;
        lockOwner = tryAcquireRefreshLock();
      }
      try {
        return await tryRefreshToken();
      } finally {
        if (lockOwner) clearRefreshLock(lockOwner);
      }
    } finally {
      isRefreshing = false;
    }
  })();

  inFlightRefresh = promise;
  try {
    const result = await promise;
    // If the interceptor queued any 401-failed requests while we were
    // refreshing, drain them now (the interceptor only drains its own queue
    // when the interceptor itself initiated the refresh).
    if (result) {
      processQueue(null, result);
    } else {
      processQueue(new SessionExpiredError(SESSION_EXPIRED_DEFAULT_MESSAGE));
    }
    return result;
  } catch (err) {
    processQueue(err instanceof Error ? err : new Error(String(err)));
    throw err;
  } finally {
    inFlightRefresh = null;
  }
}

/**
 * Check if a request should skip token refresh attempt
 * (e.g., the refresh endpoint itself, or requests that already failed refresh)
 */
const shouldSkipRefresh = (config: InternalAxiosRequestConfig): boolean => {
  // Skip if this is the refresh token request itself
  if (config.url?.includes('refresh-token')) {
    return true;
  }
  // Skip if explicitly marked to skip auth
  if ((config as IeHubInternalAxiosRequestConfig).skipAuth === true) {
    return true;
  }
  // Skip if already retried
  if ((config as IeHubInternalAxiosRequestConfig)._retry === true) {
    return true;
  }
  return false;
};

// ============================================================================
// END TOKEN REFRESH MANAGEMENT
// ============================================================================

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isSessionExpiredPayload = (value: unknown): value is SessionExpiredPayload =>
  isRecord(value) && value.sessionExpired === 'Y';

const emitUnauthorizedEvent = (detail: UnauthorizedEventDetail): void => {
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail }));
    } catch (eventError) {
      console.warn('Failed to emit unauthorized event', eventError);
    }
  }
};

const handleSessionExpiry = (response: AxiosResponse): SessionExpiredError => {
  const url = response.config?.url;
  const method = response.config?.method;
  const payload = isSessionExpiredPayload(response.data) ? response.data : undefined;
  const title = typeof payload?.errorTitle === 'string' ? payload.errorTitle : undefined;
  const message =
    typeof payload?.errorMessage === 'string' && payload.errorMessage.trim().length > 0
      ? payload.errorMessage
      : SESSION_EXPIRED_DEFAULT_MESSAGE;
  const sessionExpiredError = new SessionExpiredError(message, url, method);

  try {
    safeLocalStorageRemoveItem('authToken');
  } catch (storageError) {
    console.warn('Failed to clear auth token after session expiry', storageError);
  }

  emitUnauthorizedEvent({
    reason: message,
    ...(url !== undefined && { url }),
    ...(method !== undefined && { method }),
  });

  femaCapture('auth_session_expired', sessionExpiredError, {
    category: 'auth',
    severity: 'warning',
    context: { url, method, title },
    skipSanitization: false,
    user: {},
  });

  return sessionExpiredError;
};

// Create axios instance with base configuration
const createHttpClient = () => {
  const config = getConfig();
  
  const instance = axios.create({
    baseURL: (config.api.baseURL || '').replace(/\/+$/, ''),
    timeout: config.api.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Allowlist of cacheable endpoints (paths only, without query params)
  const CACHEABLE_ENDPOINTS: ReadonlySet<string> = new Set<string>([
    onboardingEndpoints.allAccountTypes,
    customerEndpoints.listBroadbandSuppliers,
    customerEndpoints.listLandlineSuppliers,
    customerEndpoints.listMobileSuppliers,
    // Energy companies endpoint is a function; use base path for caching
    'api/v1/ie-energy-companies-mstr',
    customerEndpoints.listLenders,
    customerEndpoints.listDigitalLenders,
    customerEndpoints.listFreepostLenders,
  ]);

  const isCacheableGet = (method: string | undefined, url: string | undefined): url is string => {
    if (typeof url !== 'string') {
      return false;
    }
    if (url.length === 0) {
      return false;
    }

    const httpMethod = (method ?? 'get').toLowerCase();
    if (httpMethod !== 'get') return false;

    // url is a relative path (validated below), ensure we compare path without query/hash
    const cleanUrl = url.split('?')[0]?.split('#')[0] ?? url;
    return CACHEABLE_ENDPOINTS.has(cleanUrl);
  };

  // Wrap adapter to add transparent localStorage caching for allowlisted GETs
  const baseAdapter = (instance.defaults.adapter ?? axios.defaults.adapter) as unknown;
  if (baseAdapter !== undefined) {
    instance.defaults.adapter = async (adapterConfig) => {
      // Enforce relative URLs only (existing validator runs in request interceptor too)
      const url = adapterConfig.url;
      const method = adapterConfig.method;
      if (isCacheableGet(method, url)) {
        const cacheKey = buildCacheKey(url, adapterConfig.params);
        const cached = readFromCache<unknown>(cacheKey, DEFAULT_MAX_AGE_MS);
        if (cached !== null) {
          const response: AxiosResponse = {
            data: cached,
            status: 200,
            statusText: 'OK',
            headers: { 'x-cache': 'hit' },
            config: adapterConfig,
            // 'request' type is not used by callers; keep minimal stub
            request: undefined as unknown as XMLHttpRequest,
          };
          return Promise.resolve(response);
        }
      }

      let networkResponse: AxiosResponse;
      if (typeof baseAdapter === 'function') {
        networkResponse = await (baseAdapter as (cfg: any) => Promise<AxiosResponse>)(adapterConfig as any);
      } else {
        // Fallback: use Axios default request with adapter unset to avoid recursion
        const { adapter: _adapter, ...rest } = adapterConfig as unknown as Record<string, unknown>;
        networkResponse = await axios.request(rest as any);
      }
      if (networkResponse && isCacheableGet(method, url) && networkResponse.status >= 200 && networkResponse.status < 300) {
        const cacheKey = buildCacheKey(url, adapterConfig.params);
        writeToCache(cacheKey, networkResponse.data);
      }
      return networkResponse;
    };
  }

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      // Enforce relative URLs only
      if (config.url) {
        validateUrl(config.url);
      }

      // Restrict allowed methods
      const allowedMethods = ['get', 'post', 'put', 'delete', 'patch'];
      if (config.method && !allowedMethods.includes(config.method.toLowerCase())) {
        throw new Error(`HTTP method ${config.method} is not allowed`);
      }

      // Debug logging for budget updates
      if (config.url && (config.url.includes('/income/') || config.url.includes('/income-notes/')) && 
          (config.method === 'put' || config.method === 'patch')) {
        console.log('🔄 Budget API Request:', {
          url: config.url,
          method: config.method,
          data: config.data
        });
      }
      // Add auth token if available (skip for legacy endpoints)
      const skipAuth = (config as IeHubInternalAxiosRequestConfig).skipAuth === true;
      if (!skipAuth) {
        const token = safeLocalStorageGetItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      // Pass the admin key for the gated debug/admin log viewers in the
      // Authorization header (scheme "AdminKey"), OVERRIDING any user Bearer token
      // set above — these endpoints are gated by the key alone (no login), so the
      // header is free to carry it. Authorization is already on the cross-origin
      // nginx proxy's CORS allow-list, unlike a custom X-Admin-Key header (blocked
      // by preflight); and unlike a query param it never lands in access logs. The
      // backend (requireAdminKey) reads the key from this header.
      if (config.url && needsAdminKey(config.url)) {
        const adminKey = safeSessionStorageGetItem(ADMIN_KEY_STORAGE_KEY);
        if (adminKey) {
          config.headers.Authorization = `AdminKey ${adminKey}`;
        }
      }

      // FormData must be sent as multipart; drop default JSON Content-Type so the
      // runtime sets multipart/form-data with the correct boundary.
      if (config.data instanceof FormData) {
        const headers = config.headers;
        if (headers instanceof AxiosHeaders) {
          headers.delete('Content-Type');
        } else if (headers && typeof headers === 'object') {
          delete (headers as Record<string, unknown>)['Content-Type'];
          delete (headers as Record<string, unknown>)['content-type'];
        }
      }

      return config;
    },
    (error) => {
      femaCapture('http_request_error', error, {
        category: 'network',
        severity: 'error',
        skipSanitization: false,
        user: {},
      });
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => {
      // Log successful responses (dev only)
      if (import.meta.env.DEV) {
        console.warn('HTTP Response:', {
          status: response.status,
          url: response.config.url,
          data: response.data,
        });
      }

      if (isSessionExpiredPayload(response.data)) {
        const sessionExpiredError = handleSessionExpiry(response);
        return Promise.reject(sessionExpiredError);
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config as IeHubInternalAxiosRequestConfig;
      
      // Handle common HTTP errors
      const errorData = {
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
        message: error.message,
      };

      // Handle 401 Unauthorized - attempt token refresh before logging out
      if (error.response?.status === 401) {
        // Special-case: the admin/debug log viewers are gated by the admin KEY,
        // not the login session (see needsAdminKey / requireAdminKey). A 401 here
        // means the key was rejected — or a not-yet-updated backend still runs
        // `authenticate` on these routes — NOT that the operator's session
        // expired. It must never trigger a token refresh, session-expiry event,
        // or logout/redirect (which would nuke an unrelated logged-in session and
        // bounce the operator to /auth). Reject as-is so the RequireAdminKey gate
        // surfaces "That key was not accepted." without disturbing any session.
        if (needsAdminKey(originalRequest.url)) {
          return Promise.reject(error);
        }

        // Special-case: logout should not trigger refresh/session-expired rewriting.
        // Callers (AuthService) may want to handle 401 and retry with a different token.
        if (typeof originalRequest.url === 'string' && originalRequest.url.includes('/auth/logout')) {
          return Promise.reject(error);
        }

        // Backend marks token-related 401s with `sessionExpired: 'Y'` and
        // in-session 401s (e.g. WRONG_PASSWORD on change-password,
        // INVALID_CREDENTIALS on signin) with 'N'. The latter must NOT
        // trigger a refresh-and-retry — the caller's JWT is still valid;
        // the request just had a logic-level auth failure that should
        // propagate to the caller as-is.
        const responseBody = error.response?.data as { sessionExpired?: string } | undefined;
        if (responseBody?.sessionExpired === 'N') {
          return Promise.reject(error);
        }

        // Check if we should skip refresh attempt
        if (shouldSkipRefresh(originalRequest)) {
          console.log('🔐 401 Error: Skipping refresh for this request');
          return handleUnauthorizedError(error);
        }

        // Cheap pre-check: another tab may have already refreshed while
        // this request was in flight. If the token in storage differs
        // from the one we sent, just retry with the current token.
        const tokenAtRequestTime =
          typeof originalRequest.headers?.Authorization === 'string'
            ? originalRequest.headers.Authorization.replace(/^Bearer\s+/i, '')
            : null;
        const storedNow = safeLocalStorageGetItem('authToken');
        if (storedNow && storedNow !== tokenAtRequestTime) {
          console.log('🔐 401 Error: Newer token in storage, retrying with it');
          originalRequest._retry = true;
          originalRequest.headers.Authorization = `Bearer ${storedNow}`;
          return instance.request(originalRequest);
        }

        // If already refreshing in this tab, queue this request
        if (isRefreshing) {
          console.log('🔐 401 Error: Token refresh in progress (this tab), queuing request');
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, config: originalRequest });
          });
        }

        // Try to acquire the cross-tab refresh lock. If another tab is
        // already refreshing, wait for it to finish rather than racing
        // (which would submit a stale refresh token and trip the
        // backend's reuse-detection).
        let lockOwner = tryAcquireRefreshLock();
        if (!lockOwner) {
          console.log('🔐 401 Error: Another tab is refreshing, waiting for new token');
          const newToken = await waitForOtherTabRefresh(tokenAtRequestTime);
          if (newToken) {
            originalRequest._retry = true;
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance.request(originalRequest);
          }
          // Other tab's refresh failed or timed out. Try once more to
          // claim the lock ourselves before falling through unsynchronised
          // — by now the prior holder's lock should have gone stale.
          lockOwner = tryAcquireRefreshLock();
        }

        // Mark as refreshing and attempt refresh
        isRefreshing = true;
        originalRequest._retry = true;

        try {
          const newToken = await tryRefreshToken();

          if (newToken) {
            // Token refreshed successfully - process queued requests and retry original
            console.log('🔐 401 Error: Token refreshed, retrying original request');
            processQueue(null, newToken);

            // Update the original request with new token and retry
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance.request(originalRequest);
          } else {
            // Refresh failed - no valid token
            console.log('🔐 401 Error: Token refresh failed, logging out');
            const logoutError = new SessionExpiredError(
              SESSION_EXPIRED_DEFAULT_MESSAGE,
              originalRequest.url,
              originalRequest.method
            );
            processQueue(logoutError);
            return handleUnauthorizedError(error);
          }
        } catch (refreshError) {
          // Refresh threw an error
          console.log('🔐 401 Error: Token refresh threw error', refreshError);
          const logoutError = new SessionExpiredError(
            SESSION_EXPIRED_DEFAULT_MESSAGE,
            originalRequest.url,
            originalRequest.method
          );
          processQueue(logoutError);
          return handleUnauthorizedError(error);
        } finally {
          isRefreshing = false;
          if (lockOwner) clearRefreshLock(lockOwner);
        }
      } else if (error.response?.status >= 500) {
        // Server errors
        femaCapture('server_error', error, {
          category: 'server',
          severity: 'error',
          context: errorData,
          skipSanitization: false,
          user: {},
        });
      } else if (error.code === 'ECONNABORTED') {
        // Timeout errors
        femaCapture('request_timeout', error, {
          category: 'network',
          severity: 'warning',
          context: errorData,
          skipSanitization: false,
          user: {},
        });
      } else {
        // Other errors
        femaCapture('http_response_error', error, {
          category: 'network',
          severity: 'error',
          context: errorData,
          skipSanitization: false,
          user: {},
        });
      }

      return Promise.reject(error);
    }
  );

  /**
   * Handle unauthorized error - emit event and create SessionExpiredError
   * This is called when token refresh fails or is not possible
   */
  function handleUnauthorizedError(error: any): Promise<never> {
    safeLocalStorageRemoveItem('authToken');

    // Show user-friendly session timeout message for 401 errors
    // (but suppress it during explicit logout flows like manual logout / account-locked logout).
    const detail: UnauthorizedEventDetail = {
      reason: SESSION_EXPIRED_DEFAULT_MESSAGE,
      ...(error.config?.url !== undefined && { url: error.config.url }),
      ...(error.config?.method !== undefined && { method: error.config.method }),
    };

    try {
      const logoutSource = getLogoutSource();
      const shouldEmitUnauthorized =
        logoutSource === null || logoutSource === 'system';

      if (shouldEmitUnauthorized) {
        emitUnauthorizedEvent(detail);
      }
    } catch (eventError) {
      console.warn('Failed to emit unauthorized event', eventError);
    }

    femaCapture('auth_token_expired', error, {
      category: 'auth',
      severity: 'warning',
      skipSanitization: false,
      user: {},
    });

    const sessionExpiredError = new SessionExpiredError(
      detail.reason,
      detail.url,
      detail.method
    );
    (sessionExpiredError as { cause?: unknown }).cause = error;
    (sessionExpiredError as { status?: number }).status = 401;
    return Promise.reject(sessionExpiredError);
  }

  return instance;
};

// Export singleton instance
export const httpClient = createHttpClient();

// In-flight GET de-duplication
// Ensures that identical concurrent GET requests share the same Promise
// and only hit the network once. Once resolved/rejected, the entry is removed.
const inFlightGetRequests = new Map<string, Promise<AxiosResponse<any>>>();

const normaliseParams = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normaliseParams);
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const sortedKeys = Object.keys(record).sort();
    const normalised: Record<string, unknown> = {};

    for (const key of sortedKeys) {
      normalised[key] = normaliseParams(record[key]);
    }

    return normalised;
  }

  return value;
};

const buildInFlightGetKey = (url: string, config?: AxiosRequestConfig): string => {
  const cleanUrl = url.split('#')[0] ?? url;

  const rawParams = config?.params;
  let paramsKey = '';

  if (rawParams instanceof URLSearchParams) {
    paramsKey = rawParams.toString();
  } else if (rawParams !== undefined) {
    try {
      paramsKey = JSON.stringify(normaliseParams(rawParams));
    } catch {
      // Fallback to string coercion if serialisation fails for any reason
      paramsKey = String(rawParams);
    }
  }

  const responseType = config?.responseType ?? '';

  return `get|${cleanUrl}|${paramsKey}|${responseType}`;
};

// Utility functions for common HTTP operations
export const httpGet = <T = any>(
  url: string,
  config: HttpRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  const key = buildInFlightGetKey(url, config);
  const existing = inFlightGetRequests.get(key) as Promise<AxiosResponse<T>> | undefined;

  if (existing) {
    return existing;
  }

  const requestPromise: Promise<AxiosResponse<T>> = httpClient
    .get<T>(url, config)
    .finally(() => {
      inFlightGetRequests.delete(key);
    });

  inFlightGetRequests.set(key, requestPromise);

  return requestPromise;
};

export const httpPost = <T = any>(url: string, data?: any, config: HttpRequestConfig = {}): Promise<AxiosResponse<T>> =>
  httpClient.post<T>(url, data, config);

export const httpPut = <T = any>(url: string, data?: any, config: HttpRequestConfig = {}): Promise<AxiosResponse<T>> =>
  httpClient.put<T>(url, data, config);

export const httpDelete = <T = any>(url: string, config: HttpRequestConfig = {}): Promise<AxiosResponse<T>> =>
  httpClient.delete<T>(url, config);

export const httpPatch = <T = any>(url: string, data?: any, config: HttpRequestConfig = {}): Promise<AxiosResponse<T>> =>
  httpClient.patch<T>(url, data, config);

// URL validation for security
export const validateUrl = (url: string): boolean => {
  // Only allow relative URLs for security
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
    throw new Error('Only relative URLs are allowed for security reasons');
  }
  
  // Basic URL format validation
  if (!url.startsWith('/')) {
    throw new Error('URL must start with "/" for relative paths');
  }
  
  return true;
};

// Safe HTTP wrapper that validates URLs
export const safeHttpRequest = async <T = any>(
  method: string,
  url: string,
  data: any = null,
  config: HttpRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  validateUrl(url);
  
  const allowedMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
  const upperMethod = method.toUpperCase();
  
  if (!allowedMethods.includes(upperMethod)) {
    throw new Error(`HTTP method ${method} is not allowed`);
  }
  
  switch (upperMethod) {
    case 'GET':
      return httpGet(url, config);
    case 'POST':
      return httpPost(url, data, config);
    case 'PUT':
      return httpPut(url, data, config);
    case 'DELETE':
      return httpDelete(url, config);
    case 'PATCH':
      return httpPatch(url, data, config);
    default:
      throw new Error(`Unsupported HTTP method: ${method}`);
  }
};

export default httpClient;
